//@ts-nocheck
import { useEffect, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { Box, Card, CardContent, Typography, Chip, Alert } from '@mui/material';
import { CheckCircle, XCircle, Info } from '@phosphor-icons/react';
import request from '../../../services/request';
import { getLocalTenantData } from '../../../utils/localTenantData';

/**
 * Test component for verifying per-tenant LaunchDarkly feature flag evaluation
 * Created for XGE-2484
 *
 * This component:
 * 1. Shows current tenant context
 * 2. Displays FE flag evaluation (from LaunchDarkly React SDK)
 * 3. Fetches BE flag evaluation (from test API endpoint)
 * 4. Compares both to ensure consistency
 */
const FeatureFlagTest = () => {
  const { testTenantFlags } = useFlags();
  const ldClient = useLDClient();
  const [tenant, setTenant] = useState(null);
  const [beResponse, setBeResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get tenant data from localStorage
  useEffect(() => {
    const tenantData = getLocalTenantData();
    console.log(
      '[FeatureFlagTest] Loaded tenant data from localStorage:',
      tenantData
    );
    setTenant(tenantData);
  }, []);

  // Identify tenant context to LaunchDarkly
  useEffect(() => {
    if (tenant && ldClient) {
      ldClient.identify({
        kind: 'tenant',
        key: `${tenant.tenantid}`,
        name: tenant.tenantname
      });
      console.log('[FeatureFlagTest] Identified tenant to LD:', {
        kind: 'tenant',
        key: tenant.tenantid,
        name: tenant.tenantname
      });
    }
  }, [ldClient, tenant]);

  // Fetch BE flag evaluation
  useEffect(() => {
    console.log('[FeatureFlagTest] BE fetch effect triggered. Tenant:', tenant);

    const fetchBEFlag = async () => {
      if (!tenant?.tenantid) {
        console.log(
          '[FeatureFlagTest] Skipping BE fetch - no tenant.tenantid:',
          tenant
        );
        return;
      }

      console.log('[FeatureFlagTest] Making BE API call with:', {
        tenantId: tenant.tenantid,
        tenantName: tenant.tenantname
      });

      setLoading(true);
      setError(null);

      try {
        // Use public endpoint - tenant inferred from origin header like all BE requests
        const response = await request.get('test/feature-flag/public');
        setBeResponse(response);
        console.log('[FeatureFlagTest] BE Response:', response);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        console.error('[FeatureFlagTest] BE Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBEFlag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.tenantid, tenant?.tenantname]);

  const flagsMatch = beResponse?.flagValue === testTenantFlags;

  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      <Typography variant='h4' gutterBottom>
        Feature Flag Test (XGE-2484)
      </Typography>

      <Alert severity='info' sx={{ mb: 3 }}>
        <Typography variant='body2'>
          This page tests per-tenant LaunchDarkly feature flag evaluation. The
          test flag should be enabled for some tenants and disabled for others.
        </Typography>
      </Alert>

      {/* Tenant Context */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Current Tenant Context
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant='body2'>
              <strong>Tenant ID:</strong> {tenant?.tenantid || 'Not available'}
            </Typography>
            <Typography variant='body2'>
              <strong>Tenant Name:</strong>{' '}
              {tenant?.tenantname || 'Not available'}
            </Typography>
            <Typography variant='body2'>
              <strong>Tenant Domain:</strong>{' '}
              {tenant?.tenantdomain || 'Not available'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Frontend Flag Evaluation */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Frontend (React SDK)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant='body2'>
              <strong>testTenantFlags:</strong>
            </Typography>
            <Chip
              icon={
                testTenantFlags ? (
                  <CheckCircle weight='fill' />
                ) : (
                  <XCircle weight='fill' />
                )
              }
              label={String(testTenantFlags)}
              color={testTenantFlags ? 'success' : 'error'}
              size='small'
            />
          </Box>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ mt: 1, display: 'block' }}
          >
            Evaluated by: launchdarkly-react-client-sdk
          </Typography>
        </CardContent>
      </Card>

      {/* Backend Flag Evaluation */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Backend (Node SDK)
          </Typography>
          {loading && (
            <Typography variant='body2' color='text.secondary'>
              Loading...
            </Typography>
          )}
          {error && (
            <Alert severity='error' sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
          {beResponse && !loading && (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
              >
                <Typography variant='body2'>
                  <strong>testTenantFlags:</strong>
                </Typography>
                <Chip
                  icon={
                    beResponse.flagValue ? (
                      <CheckCircle weight='fill' />
                    ) : (
                      <XCircle weight='fill' />
                    )
                  }
                  label={String(beResponse.flagValue)}
                  color={beResponse.flagValue ? 'success' : 'error'}
                  size='small'
                />
              </Box>
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                Evaluated by: @launchdarkly/node-server-sdk
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                Timestamp: {new Date(beResponse.timestamp).toLocaleString()}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Consistency Check */}
      {beResponse && !loading && (
        <Card>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Consistency Check
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {flagsMatch ? (
                <>
                  <CheckCircle size={24} weight='fill' color='#4caf50' />
                  <Typography variant='body2' color='success.main'>
                    ✓ Frontend and Backend flags match!
                  </Typography>
                </>
              ) : (
                <>
                  <XCircle size={24} weight='fill' color='#f44336' />
                  <Typography variant='body2' color='error.main'>
                    ✗ Mismatch detected! FE: {String(testTenantFlags)}, BE:{' '}
                    {String(beResponse.flagValue)}
                  </Typography>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Alert severity='info' icon={<Info />} sx={{ mt: 3 }}>
        <Typography variant='body2' gutterBottom>
          <strong>Testing Instructions:</strong>
        </Typography>
        <Typography variant='body2' component='div'>
          1. Configure the <code>testTenantFlags</code> in LaunchDarkly
          dashboard
          <br />
          2. Enable it for specific tenants using targeting rules
          <br />
          3. Login as different tenant users to verify per-tenant evaluation
          <br />
          4. Both FE and BE should show the same value for the same tenant
        </Typography>
      </Alert>
    </Box>
  );
};

export default FeatureFlagTest;
